/* eslint-disable react/no-unknown-property */
/* eslint-disable multiline-ternary */
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as THREE from 'three'
import { Canvas } from '@react-three/fiber'
import { useData } from '../context/DataContext'
import { useAnimationFrame } from '../helpers/useAnimationFrame'
import useDebounce from '../helpers/useDebounce'
import VideoControls from './VideoControls'
import { IEnv } from '../@types/env'
import { IVideoData } from '../@types/data'

interface SceneProps {
  texture: THREE.VideoTexture
}

interface IProps {
  isTransitioning: boolean
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
enum ProgressSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

const VideoPlayer: React.FC<IProps> = ({ isTransitioning }: IProps) => {
  const { REACT_APP_CAR_TYPE, REACT_APP_VIDEO_VERSION } =
    process.env as unknown as IEnv
  const containerRef = useRef<HTMLDivElement>(null)
  const innerContainerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const isSeeking = useRef<boolean>(false)
  const {
    isLowRes,
    timerProgress,
    nextChapter,
    requestedChapter,
    setRequestedChapter,
    requestedVideo,
    selectedVideo,
  } = useData()
  const [activeVideo, setActiveVideo] = useState<IVideoData>()
  const [videoUrl, setVideoUrl] = useState(
    `./videos/${REACT_APP_CAR_TYPE}-performance-720p.mp4?cb=video-v${REACT_APP_VIDEO_VERSION}`
  )
  const [videoTime, setVideoTime] = useState<number>(0)
  const [containerWidth, setContainerWidth] = useState<number>(1280)
  const [containerOffset, setContainerOffset] = useState<number>(0)
  const [progressSize, setProgressSize] = useState<ProgressSize>(
    ProgressSize.SMALL
  )
  const [showControls, setShowControls] = useState(true)
  const [timelineVisible, setTimelineVisible] = useState(true)
  const [videoEnded, setVideoEnded] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const debouncedTimelineVisible = useDebounce<boolean>(timelineVisible, 500)
  let hideTimeout: number | null

  const texture = useMemo(() => {
    if (videoRef?.current != null) {
      const newTexture = new THREE.VideoTexture(videoRef.current)
      newTexture.minFilter = THREE.LinearFilter
      newTexture.magFilter = THREE.LinearFilter
      return newTexture
    }
  }, [videoRef.current])

  useAnimationFrame(() => {
    if (videoRef.current == null) return
    setVideoTime(videoRef.current.currentTime)
  })

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()
    setTimeout(() => {
      handleResize()
    }, 250)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    handleVideoTouched()
    setTimeout(() => {
      setIsMounted(true)
    }, 500)
    if (videoRef.current == null) return
    videoRef.current.setAttribute('muted', 'true')
  }, [])

  useEffect(() => {
    if (requestedVideo == null) return
    const video = selectedVideo != null ? selectedVideo : requestedVideo
    setActiveVideo(video)
    const postFix: string = isLowRes ? '-720p' : ''
    setVideoUrl(
      `./videos/${REACT_APP_CAR_TYPE}-${video.key}${postFix}.mp4?cb=video-v${REACT_APP_VIDEO_VERSION}`
    )
  }, [requestedVideo, selectedVideo])

  useEffect(() => {
    setVideoEnded(false)
  }, [requestedChapter])

  useEffect(() => {
    if (showControls) {
      if (hideTimeout != null) {
        window.clearTimeout(hideTimeout)
        hideTimeout = null
      }
      hideTimeout = window.setTimeout(
        () => {
          if (!isSeeking.current) setShowControls(false)
        },
        debouncedTimelineVisible ? 2500 : 5000
      )
    }
  }, [showControls])

  const handleResize = (): void => {
    setTimelineVisible(window.innerWidth >= 1024)
    if (containerRef.current == null || innerContainerRef.current == null) {
      return
    }
    const { width } = containerRef.current.getBoundingClientRect()
    const { height } = innerContainerRef.current.getBoundingClientRect()
    const newWidth = (height / 9) * 16
    setContainerWidth(newWidth)
    setContainerOffset((width - newWidth) / 2)
    if (window.innerWidth >= 1024) {
      setProgressSize(ProgressSize.LARGE) // 48
    } else {
      setProgressSize(ProgressSize.SMALL) // 32
    }
  }

  const playVideo = (): void => {
    if (videoRef.current == null) return
    videoRef.current
      .play()
      .then(() => {
        setVideoEnded(false)
      })
      .catch(() => {
        console.warn('playing video failed')
      })
  }

  const handlePlayPause = (): void => {
    if (videoRef.current == null) return
    if (videoRef.current.paused) {
      if (timerProgress > 0) {
        if (nextChapter != null) {
          setRequestedChapter(nextChapter)
        }
      } else {
        if (videoEnded) {
          if (activeVideo !== undefined) {
            setRequestedChapter(activeVideo.chapters[0])
          }
        } else {
          playVideo()
        }
      }
    } else {
      videoRef.current.pause()
    }
  }

  const handleVideoTouched = (): void => {
    setShowControls(true)
  }

  const handleVideoSeeking = (isVideoSeeking: boolean): void => {
    isSeeking.current = isVideoSeeking
    if (hideTimeout != null) {
      window.clearTimeout(hideTimeout)
      hideTimeout = null
    }
    if (!isVideoSeeking) {
      hideTimeout = window.setTimeout(
        () => {
          setShowControls(false)
        },
        debouncedTimelineVisible ? 2500 : 5000
      )
    }
  }

  const handleVideoEnded = (): void => {
    setVideoEnded(true)
  }

  const VideoScene: React.FC<SceneProps> = ({ texture }) => {
    return (
      <mesh>
        <planeGeometry args={[16, 9, 1]} />
        <meshBasicMaterial attach="material" map={texture} toneMapped={false} />
      </mesh>
    )
  }

  if (requestedVideo == null) return null
  // const activeVideo = selectedVideo != null ? selectedVideo : requestedVideo

  const gradient =
    'before:absolute before:bottom-0 before:h-[125%] before:w-full before:z-[-1] before:bg-gradient-to-t before:from-gray-darkest-0.6 before:to-gray-darkest-0 lg:before:hidden'
  const progressRadius = progressSize === ProgressSize.SMALL ? 32 : 48
  const progressStrokeWidth = progressSize === ProgressSize.SMALL ? 2 : 4
  const normalizedRadius = progressRadius - progressStrokeWidth / 2
  const circumference = normalizedRadius * 2 * Math.PI
  const ringProgress = ((100 - timerProgress) / 100) * circumference

  return (
    <div className="relative flex flex-col w-full bg-gray-darkest overflow-hidden lg:h-full lg:overflow-visible">
      <div ref={containerRef} className="h-full overflow-hidden">
        <div
          ref={innerContainerRef}
          className="relative h-full pb-[56.25%]"
          style={{
            width: `${containerWidth}px`,
            transform: `translateX(${containerOffset}px)`,
          }}
        >
          <div
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
              isTransitioning ? 'opacity-0 delay-100' : 'opacity-100 delay-200'
            }`}
            onPointerDown={handleVideoTouched}
            onPointerEnter={handleVideoTouched}
            onPointerMove={handleVideoTouched}
          >
            {texture !== undefined && (
              <Canvas
                gl={{ antialias: true, toneMapping: THREE.NoToneMapping }}
                linear
              >
                <VideoScene texture={texture} />
              </Canvas>
            )}
            <video
              ref={videoRef}
              className="absolute opacity-[0.001] top-0 -z-50"
              src={videoUrl}
              autoPlay
              playsInline
              onEnded={handleVideoEnded}
            />
            <button
              className={`absolute w-16 h-16 lg:w-24 lg:h-24 rounded-full bg-gray-darkest-0.6 transition-opacity duration-300 ${
                (showControls || timerProgress > 0 || videoEnded) &&
                !isTransitioning &&
                isMounted
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              } ${isTransitioning ? 'pointer-events-none' : ''}`}
              onClick={handlePlayPause}
            >
              <svg
                className="absolute top-[0px] left-[0px] -rotate-90"
                height={`${progressRadius * 2}`}
                width={`${progressRadius * 2}`}
              >
                <circle
                  className="stroke-gold"
                  strokeDasharray={`${circumference} ${circumference}`}
                  style={{
                    strokeDashoffset: ringProgress,
                  }}
                  strokeWidth={progressStrokeWidth}
                  fill="transparent"
                  r={normalizedRadius}
                  cx={progressRadius}
                  cy={progressRadius}
                />
              </svg>
              {videoRef.current?.paused ?? false ? (
                <svg
                  className="w-full h-full"
                  width="64"
                  height="64"
                  viewBox="0 0 64 64"
                  fill="none"
                >
                  <path
                    className="fill-gray-medium"
                    d="M46 32L26 47V17L46 32Z"
                    fill="#C4C4C4"
                  />
                </svg>
              ) : (
                <svg
                  className="w-full h-full"
                  width="64"
                  height="64"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="fill-gray-medium"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M23.6667 17C22.7462 17 22 17.7462 22 18.6667V45.3333C22 46.2538 22.7462 47 23.6667 47H25.3333C26.2538 47 27 46.2538 27 45.3333V18.6667C27 17.7462 26.2538 17 25.3333 17H23.6667ZM38.6667 17C37.7462 17 37 17.7462 37 18.6667V45.3333C37 46.2538 37.7462 47 38.6667 47H40.3333C41.2538 47 42 46.2538 42 45.3333V18.6667C42 17.7462 41.2538 17 40.3333 17H38.6667Z"
                    fill="#C4C4C4"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {videoRef.current != null && (
        <div
          className="absolute bottom-0 w-full lg:relative lg:bottom-auto lg:border-t-[1px] border-gray-border transition-transform-opacity duration-700"
          onPointerDown={() => {
            if (!debouncedTimelineVisible) handleVideoTouched()
          }}
          onPointerEnter={() => {
            if (!debouncedTimelineVisible) handleVideoTouched()
          }}
          onPointerMove={() => {
            if (!debouncedTimelineVisible) handleVideoTouched()
          }}
        >
          <div
            className={`relative w-full justify-center flex px-4 py-2 lg:pr-6 lg:py-3 xl:py-4 xl:pr-8 2xl:py-6 2xl:pr-10 transition-opacity duration-1000 ${
              showControls ? 'translate-y-0' : 'translate-y-[150%]'
            } lg:translate-y-0 ${gradient} ${
              isMounted ? 'opacity-100 delay-500' : 'opacity-0'
            }`}
          >
            {activeVideo !== undefined && (
              <VideoControls
                videoEl={videoRef.current}
                video={activeVideo}
                currentTime={videoTime}
                onPlayPause={handlePlayPause}
                onSeeking={handleVideoSeeking}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoPlayer
